import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminPayouts() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agent_code: "", amount: 0, status: "Pending", notes: "" });

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/admin/payouts"), api.get("/admin/agents")]);
    setItems(p.data || []); setAgents(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.agent_code || !form.amount) { toast.error("Agent and amount required"); return; }
    await api.post("/admin/payouts", { ...form, amount: Number(form.amount) });
    toast.success("Payout recorded"); setOpen(false); setForm({ agent_code:"", amount:0, status:"Pending", notes:"" }); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payout?")) return;
    await api.delete(`/admin/payouts/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div><div className="overline">Earnings</div><h1 className="font-display text-3xl font-extrabold mt-1">Payouts</h1></div>
        <Button data-testid="payouts-add" className="btn-saffron rounded-full" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2"/>Add payout</Button>
      </div>

      <div className="mt-6 card-flat p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-ink-muted border-b border-slate-200">
            <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Agent</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Notes</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-ink-muted">No payouts.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="py-3 pr-3 whitespace-nowrap text-xs">{new Date(p.created_at).toLocaleString("en-IN", { dateStyle: "medium" })}</td>
                <td className="py-3 pr-3 font-mono font-bold text-saffron">{p.agent_code}</td>
                <td className="py-3 pr-3 font-semibold">₹{Number(p.amount).toLocaleString("en-IN")}</td>
                <td className="py-3 pr-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "Paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                <td className="py-3 pr-3 text-xs text-ink-muted">{p.notes}</td>
                <td className="py-3 pr-3"><Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-red-500"/></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payout</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Agent">
              <Select value={form.agent_code} onValueChange={v => setForm({ ...form, agent_code: v })}>
                <SelectTrigger data-testid="payout-agent"><SelectValue placeholder="Select agent"/></SelectTrigger>
                <SelectContent>{agents.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount (₹)"><Input data-testid="payout-amount" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}/></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="payout-status"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Notes"><Input data-testid="payout-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}/></Field>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="payout-save" className="btn-saffron rounded-full" onClick={save}>Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
